import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import InfoDialog from '../components/common/InfoDialog';
import {
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { Add, Search, Edit, Delete, Visibility, VisibilityOff, Public, Lock, MoreVert } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useProject } from '../contexts/ProjectContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { usePermissions } from '../hooks/usePermissions';
import UpgradePrompt from '../components/UpgradePrompt';
import api from '../services/api';

const ProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const statusColors = {
  planning: 'warning',
  in_progress: 'primary',
  completed: 'success',
  on_hold: 'default',
  cancelled: 'error',
};

const statusLabels = {
  planning: 'Planning',
  in_progress: 'In Progress',
  completed: 'Completed',
  on_hold: 'On Hold',
  cancelled: 'Cancelled',
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { projects, loading, error, deleteProject } = useProject();
  const { currentOrganization } = useOrganization();
  const permissions = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [statusFilter, setStatusFilter] = useState('all');
  const [visibilityFilter, setVisibilityFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [infoDialog, setInfoDialog] = useState({ open: false, title: '', message: '', severity: 'info' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [projectLimit, setProjectLimit] = useState(null);

  useEffect(() => {
    if (!currentOrganization?.id) return;
    api.get(`/orgs/${currentOrganization.id}/usage`)
      .then((res) => {
        const limit = res.data?.limits?.projects ?? null;
        setProjectLimit(limit);
      })
      .catch(() => {});
  }, [currentOrganization?.id]);

  const statusOrder = { planning: 0, in_progress: 1, completed: 2, on_hold: 3, cancelled: 4 };

  const filteredProjects = projects
    .filter((project) => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        project.name.toLowerCase().includes(term) ||
        (project.description && project.description.toLowerCase().includes(term));
      const matchesStatus =
        statusFilter === 'all' || project.status === statusFilter;
      const matchesVisibility =
        visibilityFilter === 'all' ||
        (visibilityFilter === 'public' && project.is_public) ||
        (visibilityFilter === 'private' && !project.is_public);
      return matchesSearch && matchesStatus && matchesVisibility;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'status')
        return (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (sortBy === 'visibility')
        return (b.is_public ? 1 : 0) - (a.is_public ? 1 : 0);
      return 0;
    });

  const handleMenuOpen = (event, project) => {
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleEdit = () => {
    if (selectedProject) {
      navigate(`/projects/edit/${selectedProject.id}`);
      handleMenuClose();
    }
  };

  const handleDelete = async () => {
    if (selectedProject && window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(selectedProject.id);
      } catch (err) {
        console.error('Error deleting project:', err);
      }
      handleMenuClose();
    }
  };

  const handleCreateProject = async () => {
    if (!currentOrganization?.id) {
      setInfoDialog({
        open: true,
        title: 'Organization Required',
        message: 'Please select an organization first before creating a project.',
        severity: 'warning',
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1'}/orgs/${currentOrganization.id}/usage/projects/check`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json();
      if (!data.is_within_limit) {
        setShowUpgradePrompt(true);
        return;
      }
    } catch (err) {
      // fail open
    }
    navigate('/projects/new');
  };

  if (loading && projects.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  const projectCount = projects.length;
  const countLabel = projectLimit !== null
    ? `${projectCount} of ${projectLimit} projects`
    : `${projectCount} project${projectCount !== 1 ? 's' : ''}`;

  return (
    <Box sx={{ width: '100%', py: { xs: 2, md: 3 }, maxWidth: '1600px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Projects
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {countLabel}
          </Typography>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateProject}>
            New Project
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Sort by</InputLabel>
          <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} label="Sort by">
            <MenuItem value="name">Name (A–Z)</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="visibility">Visibility</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="all">All Statuses</MenuItem>
            <MenuItem value="planning">Planning</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="on_hold">On Hold</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Visibility</InputLabel>
          <Select value={visibilityFilter} onChange={(e) => setVisibilityFilter(e.target.value)} label="Visibility">
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {filteredProjects.length === 0 ? (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {searchTerm || statusFilter !== 'all' || visibilityFilter !== 'all'
              ? 'No projects match your filters'
              : 'No projects yet'}
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {searchTerm || statusFilter !== 'all' || visibilityFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first project'}
          </Typography>
          {permissions.canCreateProjects && (
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleCreateProject}>
              Create Project
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
            <Grid item key={project.id} xs={12} sm={6} md={4}>
              <ProjectCard>
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box display="flex" alignItems="center" gap={1} maxWidth="75%">
                      <Typography variant="h6" component="h2" noWrap>
                        {project.name}
                      </Typography>
                      <Tooltip title={`Project is ${project.is_public ? 'Public' : 'Private'}`} placement="top">
                        <Box component="span">
                          {project.is_public ? (
                            <Public fontSize="small" color="success" />
                          ) : (
                            <Lock fontSize="small" color="disabled" />
                          )}
                        </Box>
                      </Tooltip>
                      <Tooltip title={`Work Items default is ${project.task_default_public ? 'Public' : 'Private'}`} placement="top">
                        <Box component="span">
                          {project.task_default_public ? (
                            <Visibility fontSize="small" color="action" />
                          ) : (
                            <VisibilityOff fontSize="small" color="disabled" />
                          )}
                        </Box>
                      </Tooltip>
                    </Box>
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, project)} aria-label="project actions">
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {project.status && (
                    <Chip
                      label={statusLabels[project.status] || project.status}
                      size="small"
                      color={statusColors[project.status] || 'default'}
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minHeight: '60px',
                    }}
                  >
                    {project.description || 'No description provided'}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0, pb: 2, px: 2 }}>
                  <Button size="small" startIcon={<Visibility />} onClick={() => navigate(`/projects/${project.id}`)}>
                    View
                  </Button>
                  <Button size="small" startIcon={<Edit />} onClick={() => navigate(`/projects/edit/${project.id}`)}>
                    Edit
                  </Button>
                </CardActions>
              </ProjectCard>
            </Grid>
          ))}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit Project
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete Project
        </MenuItem>
      </Menu>

      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="projects"
        currentPlan={currentOrganization?.subscription?.plan?.display_name || 'Free'}
        requiredPlan="Basic"
      />

      <InfoDialog
        open={infoDialog.open}
        onClose={() => setInfoDialog({ open: false, title: '', message: '', severity: 'info' })}
        title={infoDialog.title}
        message={infoDialog.message}
        severity={infoDialog.severity}
      />
    </Box>
  );
};

export default ProjectsPage;
